<template>
  <div class="bg-gray-50">
    <div class="space-y-4">
      <!-- 搜索和筛选区域 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div class="p-4">
          <!-- 搜索和筛选区域 - 单行布局 -->
          <div class="flex flex-col lg:flex-row gap-4">
            <!-- 搜索框 -->
            <div class="flex-1">
              <div class="relative">
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="搜索邮箱、用户名、部门或职位..."
                  class="w-full h-10 pl-10 pr-4 text-sm bg-gray-50/50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all duration-200 placeholder-gray-400"
                  @keyup.enter="loadSubAccounts"
                >
                <MagnifyingGlassIcon class="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <!-- 状态筛选 -->
            <div class="w-full sm:w-auto">
              <select
                v-model="statusFilter"
                @change="loadSubAccounts"
                class="w-full sm:w-32 h-10 px-3 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all duration-200"
              >
                <option value="">所有状态</option>
                <option value="true">启用</option>
                <option value="false">禁用</option>
              </select>
            </div>

            <!-- 搜索按钮 -->
            <button
              @click="loadSubAccounts"
              :disabled="loading"
              class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MagnifyingGlassIcon class="h-4 w-4 mr-2" />
              搜索
            </button>

            <!-- 配额信息 -->
            <div class="flex items-center bg-blue-50 rounded-lg px-3 border border-blue-200 whitespace-nowrap">
              <span class="text-sm text-gray-600 mr-2">子账户配额:</span>
              <span class="font-medium text-blue-900">
                {{ quota.current_sub_accounts || 0 }}/{{ quota.max_sub_accounts || 0 }}
              </span>
              <div class="ml-3 w-16 bg-blue-100 rounded-full h-1.5">
                <div 
                  class="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                  :style="{ width: `${Math.min(quotaPercentage, 100)}%` }"
                ></div>
              </div>
            </div>

            <!-- 创建子账户按钮 -->
            <button
              @click="showCreateModal = true"
              :disabled="!quota.can_create_sub_account"
              class="inline-flex items-center justify-center h-10 px-4 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 shadow-sm hover:shadow-md transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              <PlusIcon class="h-4 w-4 mr-2" />
              创建子账户
            </button>
          </div>
        </div>
      </div>

      <!-- 子账户列表 -->
      <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <svg class="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-gray-500">加载中...</p>
          </div>
        </div>

        <!-- 空状态 -->
        <div v-else-if="subAccounts.length === 0" class="text-center py-20">
          <UsersIcon class="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 class="text-lg font-medium text-gray-900 mb-2">
            {{ searchQuery || statusFilter ? '无匹配结果' : '暂无子账户' }}
          </h3>
          <p class="text-gray-500 mb-6">
            {{ searchQuery || statusFilter ? '尝试调整搜索条件或筛选条件' : '开始创建您的第一个子账户' }}
          </p>
          <button
            @click="showCreateModal = true"
            :disabled="!quota.can_create_sub_account"
            class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon class="h-4 w-4 mr-2" />
            创建子账户
          </button>
        </div>

        <!-- 子账户数据内容 -->
        <div v-if="!loading && subAccounts.length > 0">
          <!-- 子账户表格 -->
          <div class="overflow-x-auto">
          <table class="w-full table-auto divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="pl-6 pr-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[280px]">
                  账户信息
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  状态
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[200px]">
                  权限配置
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[140px]">
                  创建时间
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[160px]">
                  操作
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="account in subAccounts" :key="account.id" class="hover:bg-gray-50">
                <!-- 账户信息 -->
                <td class="pl-6 pr-6 py-4 whitespace-nowrap w-[280px]">
                  <div class="flex items-center">
                    <div class="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                      <span class="text-white font-medium text-sm">
                        {{ account.email.charAt(0).toUpperCase() }}
                      </span>
                    </div>
                    <div class="ml-3 min-w-0">
                      <div class="text-sm font-medium text-gray-900 truncate">{{ account.username }}</div>
                      <div class="text-sm text-gray-500 truncate">{{ account.email }}</div>
                      <div v-if="account.department || account.position" class="text-xs text-gray-400 truncate">
                        {{ [account.department, account.position].filter(Boolean).join(' • ') }}
                      </div>
                    </div>
                  </div>
                </td>

                <!-- 状态 -->
                <td class="px-6 py-4 whitespace-nowrap w-[100px]">
                  <span
                    :class="[
                      'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                      account.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    ]"
                  >
                    <span
                      :class="[
                        'w-1.5 h-1.5 rounded-full mr-1.5',
                        account.is_active ? 'bg-green-400' : 'bg-red-400'
                      ]"
                    ></span>
                    {{ account.is_active ? '启用' : '禁用' }}
                  </span>
                </td>

                <!-- 权限配置 -->
                <td class="px-6 py-4 w-[200px]">
                  <div class="flex flex-wrap gap-1">
                    <span v-if="account.accessible_email_accounts?.length > 0" class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 font-medium">
                      邮件账户 {{ account.accessible_email_accounts.length }}
                    </span>
                    <span v-if="account.accessible_customer_groups?.length > 0" class="inline-flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-800 font-medium">
                      客户分组 {{ account.accessible_customer_groups.length }}
                    </span>
                    <span v-if="(!account.accessible_email_accounts?.length && !account.accessible_customer_groups?.length)" class="text-gray-400 text-xs">
                      无权限
                    </span>
                  </div>
                </td>

                <!-- 创建时间 -->
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-[140px]">
                  {{ formatDate(account.created_at) }}
                </td>

                <!-- 操作 -->
                <td class="px-6 py-4 whitespace-nowrap text-sm w-[160px]">
                  <div class="flex items-center space-x-1">
                    <button
                      @click="editAccount(account)"
                      class="inline-flex items-center text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded text-xs transition-colors"
                      title="编辑账户"
                    >
                      <PencilSquareIcon class="h-4 w-4 mr-1" />
                      编辑
                    </button>
                    <button
                      @click="editPermissions(account)"
                      class="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs transition-colors"
                      title="权限配置"
                    >
                      <CogIcon class="h-4 w-4 mr-1" />
                      权限
                    </button>
                    <button
                      @click="toggleAccountStatus(account)"
                      :disabled="isOperationLoading(account.id, 'toggle')"
                      :class="[
                        'inline-flex items-center px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                        account.is_active 
                          ? 'text-orange-600 hover:text-orange-900 bg-orange-50 hover:bg-orange-100' 
                          : 'text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100'
                      ]"
                      :title="account.is_active ? '禁用账户' : '启用账户'"
                    >
                      <svg v-if="isOperationLoading(account.id, 'toggle')" class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <component v-else :is="account.is_active ? PauseIcon : PlayIcon" class="h-4 w-4 mr-1" />
                      {{ account.is_active ? '禁用' : '启用' }}
                    </button>
                    <button
                      @click="deleteAccount(account)"
                      :disabled="isOperationLoading(account.id, 'delete')"
                      class="inline-flex items-center text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="删除账户"
                    >
                      <svg v-if="isOperationLoading(account.id, 'delete')" class="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <TrashIcon v-else class="h-4 w-4 mr-1" />
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 手机端卡片列表 -->
        <div class="md:hidden divide-y divide-gray-200">
          <div v-for="account in subAccounts" :key="account.id" class="p-4 bg-white hover:bg-gray-50 transition-colors">
            <!-- 账户基本信息 -->
            <div class="flex items-start justify-between mb-3">
              <div class="flex items-center flex-1 min-w-0">
                <div class="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                  <span class="text-white font-medium text-sm">
                    {{ account.email.charAt(0).toUpperCase() }}
                  </span>
                </div>
                <div class="ml-3 min-w-0 flex-1">
                  <div class="text-sm font-medium text-gray-900 truncate">{{ account.username }}</div>
                  <div class="text-sm text-gray-500 truncate">{{ account.email }}</div>
                  <div v-if="account.department || account.position" class="text-xs text-gray-400 truncate mt-1">
                    {{ [account.department, account.position].filter(Boolean).join(' • ') }}
                  </div>
                </div>
              </div>
              
              <!-- 状态标签 -->
              <div class="ml-3 flex-shrink-0">
                <span
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                    account.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  ]"
                >
                  <span
                    :class="[
                      'w-1.5 h-1.5 rounded-full mr-1.5',
                      account.is_active ? 'bg-green-400' : 'bg-red-400'
                    ]"
                  ></span>
                  {{ account.is_active ? '启用' : '禁用' }}
                </span>
              </div>
            </div>
            
            <!-- 权限信息 -->
            <div class="mb-3">
              <div class="flex flex-wrap gap-1">
                <span v-if="account.accessible_email_accounts?.length > 0" class="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 font-medium">
                  邮件账户 {{ account.accessible_email_accounts.length }}
                </span>
                <span v-if="account.accessible_customer_groups?.length > 0" class="inline-flex items-center px-2 py-1 text-xs bg-orange-100 text-orange-800 font-medium">
                  客户分组 {{ account.accessible_customer_groups.length }}
                </span>
                <span v-if="(!account.accessible_email_accounts?.length && !account.accessible_customer_groups?.length)" class="text-gray-400 text-xs">
                  无权限
                </span>
              </div>
            </div>
            
            <!-- 创建时间 -->
            <div class="text-xs text-gray-500 mb-3">
              创建时间: {{ formatDate(account.created_at) }}
            </div>
            
            <!-- 操作按钮 -->
            <div class="flex items-center space-x-2">
              <button
                @click="editAccount(account)"
                class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 hover:border-indigo-300 transition-all duration-200"
              >
                <PencilSquareIcon class="h-4 w-4 mr-1.5" />
                编辑
              </button>
              <button
                @click="editPermissions(account)"
                class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 transition-all duration-200"
              >
                <CogIcon class="h-4 w-4 mr-1.5" />
                权限配置
              </button>
              <button
                @click="toggleAccountStatus(account)"
                :disabled="isOperationLoading(account.id, 'toggle')"
                :class="[
                  'flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium border rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
                  account.is_active 
                    ? 'text-orange-600 bg-orange-50 border-orange-200 hover:bg-orange-100 hover:border-orange-300' 
                    : 'text-green-600 bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300'
                ]"
              >
                <svg v-if="isOperationLoading(account.id, 'toggle')" class="animate-spin h-4 w-4 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <component v-else :is="account.is_active ? PauseIcon : PlayIcon" class="h-4 w-4 mr-1.5" />
                {{ account.is_active ? '禁用' : '启用' }}
              </button>
              <button
                @click="deleteAccount(account)"
                :disabled="isOperationLoading(account.id, 'delete')"
                class="inline-flex items-center justify-center w-10 h-10 text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 hover:border-red-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="删除"
              >
                <svg v-if="isOperationLoading(account.id, 'delete')" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <TrashIcon v-else class="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

        <!-- 分页 -->
        <div v-if="pagination.total > 0" class="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-700">
              显示第 {{ (pagination.page - 1) * pagination.pageSize + 1 }}-{{ Math.min(pagination.page * pagination.pageSize, pagination.total) }} 条，
              共 {{ pagination.total }} 条记录
            </div>
            <div class="flex space-x-1">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="pagination.page <= 1"
                class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="pagination.page >= Math.ceil(pagination.total / pagination.pageSize)"
                class="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建子账户模态框 -->
    <CreateSubAccountModal
      :show="showCreateModal"
      :available-resources="availableResources"
      @close="showCreateModal = false"
      @created="handleAccountCreated"
    />

    <!-- 权限配置模态框 -->
    <PermissionConfigModal
      :show="showPermissionModal"
      :account="selectedAccount"
      :available-resources="availableResources"
      @close="showPermissionModal = false"
      @updated="handlePermissionUpdated"
    />

    <!-- 编辑子账户模态框 -->
    <EditSubAccountModal
      :show="showEditModal"
      :account="selectedAccount"
      @close="showEditModal = false"
      @updated="handleAccountUpdated"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useToast } from 'vue-toastification'
import {
  PlusIcon, MagnifyingGlassIcon, UsersIcon, ArrowPathIcon,
  CogIcon, PauseIcon, PlayIcon, TrashIcon, PencilSquareIcon
} from '@heroicons/vue/24/outline'

import CreateSubAccountModal from './components/CreateSubAccountModal.vue'
import PermissionConfigModal from './components/PermissionConfigModal.vue'
import EditSubAccountModal from './components/EditSubAccountModal.vue'
import { subAccountService } from '@/services/subAccountService'

const toast = useToast()

// 响应式数据
const loading = ref(false)
const subAccounts = ref([])
const quota = ref({})
const availableResources = ref({})
const searchQuery = ref('')
const statusFilter = ref('')
const showCreateModal = ref(false)
const showPermissionModal = ref(false)
const showEditModal = ref(false)
const selectedAccount = ref(null)

// 操作loading状态
const operationLoading = ref(new Set())

// 分页信息
const pagination = ref({
  page: 1,
  pageSize: 20,
  total: 0
})

// 计算属性
const quotaPercentage = computed(() => {
  if (!quota.value.max_sub_accounts) return 0
  return (quota.value.current_sub_accounts / quota.value.max_sub_accounts) * 100
})

// 操作loading状态管理
const isOperationLoading = (accountId, operation) => {
  return operationLoading.value.has(`${accountId}-${operation}`)
}

const setOperationLoading = (accountId, operation, loading) => {
  const key = `${accountId}-${operation}`
  if (loading) {
    operationLoading.value.add(key)
  } else {
    operationLoading.value.delete(key)
  }
}

// 搜索处理
const handleSearch = () => {
  pagination.value.page = 1
  loadSubAccounts()
}

// 方法
const loadSubAccounts = async () => {
  try {
    loading.value = true
    const params = {
      page: pagination.value.page,
      page_size: pagination.value.pageSize
    }
    
    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }
    
    if (statusFilter.value !== '') {
      params.is_active = statusFilter.value
    }

    const response = await subAccountService.getSubAccounts(params)
    
    subAccounts.value = response.results || []
    pagination.value = {
      page: pagination.value.page,
      pageSize: pagination.value.pageSize,
      total: response.count || 0
    }
  } catch (error) {
    console.error('加载子账户列表失败:', error)
    toast.error('加载子账户列表失败')
  } finally {
    loading.value = false
  }
}

const loadQuota = async () => {
  try {
    const response = await subAccountService.getQuota()
    quota.value = response
  } catch (error) {
    console.error('加载配额信息失败:', error)
  }
}

const loadAvailableResources = async () => {
  try {
    const response = await subAccountService.getAvailableResources()
    availableResources.value = response
  } catch (error) {
    console.error('加载可用资源失败:', error)
  }
}

const changePage = (page) => {
  if (page >= 1 && page <= Math.ceil(pagination.value.total / pagination.value.pageSize)) {
    pagination.value.page = page
    loadSubAccounts()
  }
}

const editPermissions = (account) => {
  selectedAccount.value = account
  showPermissionModal.value = true
}

const editAccount = (account) => {
  selectedAccount.value = account
  showEditModal.value = true
}

const handleAccountUpdated = () => {
  showEditModal.value = false
  loadSubAccounts()
}

const toggleAccountStatus = async (account) => {
  try {
    const action = account.is_active ? '禁用' : '启用'
    if (!confirm(`确定要${action}子账户 ${account.email} 吗？`)) {
      return
    }

    setOperationLoading(account.id, 'toggle', true)
    await subAccountService.toggleAccountStatus(account.id)
    toast.success(`子账户${action}成功`)
    
    // 更新本地状态
    account.is_active = !account.is_active
  } catch (error) {
    console.error('切换账户状态失败:', error)
    toast.error('操作失败，请重试')
  } finally {
    setOperationLoading(account.id, 'toggle', false)
  }
}

const deleteAccount = async (account) => {
  try {
    if (!confirm(`确定要删除子账户 ${account.email} 吗？此操作不可恢复。`)) {
      return
    }

    setOperationLoading(account.id, 'delete', true)
    await subAccountService.deleteSubAccount(account.id)
    toast.success('子账户删除成功')
    
    // 立即更新前端配额数据
    if (quota.value.current_sub_accounts > 0) {
      quota.value.current_sub_accounts -= 1
      quota.value.usage_percentage_sub_accounts = Math.round((quota.value.current_sub_accounts / quota.value.max_sub_accounts) * 100)
      quota.value.can_create_sub_account = quota.value.current_sub_accounts < quota.value.max_sub_accounts
    }
    
    // 重新加载子账户列表
    loadSubAccounts()
  } catch (error) {
    console.error('删除子账户失败:', error)
    toast.error('删除失败，请重试')
  } finally {
    setOperationLoading(account.id, 'delete', false)
  }
}

const handleAccountCreated = async () => {
  showCreateModal.value = false
  
  // 立即更新前端配额数据
  if (quota.value.current_sub_accounts < quota.value.max_sub_accounts) {
    quota.value.current_sub_accounts += 1
    quota.value.usage_percentage_sub_accounts = Math.round((quota.value.current_sub_accounts / quota.value.max_sub_accounts) * 100)
    quota.value.can_create_sub_account = quota.value.current_sub_accounts < quota.value.max_sub_accounts
  }
  
  // 重新加载子账户列表
  loadSubAccounts()
}

const handlePermissionUpdated = () => {
  showPermissionModal.value = false
  toast.success('权限配置已更新')
  
  // 重新加载列表
  loadSubAccounts()
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// 组件挂载时加载数据
onMounted(() => {
  loadSubAccounts()
  loadQuota()
  loadAvailableResources()
})
</script>

<style scoped>
/* 自定义滚动条样式 */
.overflow-x-auto::-webkit-scrollbar {
  height: 6px;
}

.overflow-x-auto::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.overflow-x-auto::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.overflow-x-auto::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
